import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Box, Button, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'

const Page = ({
  children,
  rightDrawer,
  title,
  header,
  footer,
  variant = 'plain',
  afterContent,
}) => {
  const [showRightDrawer, setShowRightDrawer] = React.useState(
    rightDrawer?.open || false,
  )
  const closeButtonRef = React.useRef(null)
  const inChatFrame = parent.document.getElementById('chat_browse') !== null

  // CTS-9235: focus close button on open, trap focus via inert + Escape key when drawer is in modal mode (<3xl)
  React.useEffect(() => {
    if (showRightDrawer && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [showRightDrawer])

  React.useEffect(() => {
    if (!showRightDrawer) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowRightDrawer(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showRightDrawer])

  const gridCols = () => {
    if (inChatFrame || !rightDrawer) {
      return 'md:grid-cols-[1fr]'
    } else {
      return '3xl:grid-cols-[1fr_350px]'
    }
  }

  const getBoxVariant = () => {
    switch (variant) {
      case 'plain':
        return { variant: 'default', color: 'default' }
      case 'primary':
        return { variant: 'outlined', color: 'primary' }
      case 'primary-light':
        return { variant: 'outlined', color: 'primary-light' }
      case 'secondary':
        return { variant: 'outlined', color: 'secondary' }
      case 'secondary-light':
        return { variant: 'outlined', color: 'secondary-light' }
      case 'coach-services':
        return { variant: 'outlined', color: 'default' }
      case 'select':
        return { variant: 'outlined', color: 'select' }
      case 'select-light':
        return { variant: 'outlined', color: 'select-light' }
      default:
        return { variant: 'default', color: 'default' }
    }
  }

  const isSecondaryVariant =
    variant === 'secondary' || variant === 'secondary-light'

  const getHeaderTextColor = () => {
    if (variant === 'select')
      return '[&&]:text-white [&_strong]:text-white [&_b]:text-white'
    if (isSecondaryVariant)
      return 'text-intoo-green-medium [&_b]:text-intoo-green-medium-900 [&_strong]:text-intoo-green-medium-900'
    return undefined
  }

  const renderCoachServicesHeader = () => {
    if (variant !== 'coach-services') return null

    return (
      <div className="relative">
        <div className="absolute inset-0 z-0 min-h-[176px] bg-willow-brook" />
        <div className="relative z-10 flex flex-col p-4 md:p-9">
          <Typography
            variant="title1"
            color={getHeaderTextColor()}
            className="mb-4 !font-roboto-slab text-2xl text-[32px] font-medium !text-intoo-green-coachcta"
          >
            {title}
          </Typography>
          {header && <div className="page-header-content">{header}</div>}
        </div>
      </div>
    )
  }

  const renderCoachServicesContent = () => {
    if (variant !== 'coach-services') return null

    return (
      <div
        className={`relative z-20 mx-3 mb-4 flex flex-col p-4 font-roboto text-cod-gray-800 md:mx-8 md:mb-9 md:p-9 xl:grid ${rightDrawer ? 'xl:grid-cols-[1fr_350px]' : ''} rounded-3xl border border-cod-gray-200 bg-white xl:gap-6`}
      >
        <div className="page-content">{children}</div>
        {rightDrawer && (
          <div className="hidden rounded-3xl border border-cod-gray-200 bg-white p-6 xl:block">
            <Stack spacing={4}>
              <div className="flex items-start justify-between gap-2">
                <Typography variant="subtitle">
                  {rightDrawer.title || ''}
                </Typography>
              </div>
              <div>{rightDrawer.children}</div>
            </Stack>
          </div>
        )}
        {footer && <div className="page-footer">{footer}</div>}
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col text-cod-gray-800 ${variant === 'coach-services' ? 'min-h-0 overflow-y-auto' : 'md:grid md:p-4'} ${variant !== 'coach-services' ? gridCols() : ''} ${variant !== 'coach-services' ? 'lg:gap-6 lg:p-6' : ''}`}
    >
      {variant === 'coach-services' ? (
        <>
          {renderCoachServicesHeader()}
          {renderCoachServicesContent()}
          {afterContent && (
            <div className="relative z-20 mx-3 mb-4 md:mx-8 md:mb-9">
              {afterContent}
            </div>
          )}
          {rightDrawer && (
            <div
              className={`fixed right-0 top-0 z-40 h-screen w-96 overflow-y-auto p-4 transition-transform xl:hidden ${
                showRightDrawer ? 'transform-none' : 'translate-x-full'
              }`}
            >
              <Box variant="outlined" color="primary">
                <Stack spacing={4}>
                  <div className="flex items-start justify-between gap-2">
                    <Typography variant="subtitle">
                      {rightDrawer.title || ''}
                    </Typography>
                    {/* CTS-9234: keyboard-close for drawer */}
                    <button
                      type="button"
                      className="rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-blue-medium-700 focus-visible:ring-offset-2"
                      aria-label="Close panel"
                      onClick={() => setShowRightDrawer(false)}
                    >
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                  <div>{rightDrawer.children}</div>
                </Stack>
              </Box>
            </div>
          )}
        </>
      ) : (
        <div {...(showRightDrawer && !!rightDrawer ? { inert: '' } : {})}>
          <Stack spacing={8}>
            <Box {...getBoxVariant()}>
              <Stack spacing={8}>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    {/* CTS-9268: single page title as heading level 1 */}
                    <Typography
                      variant="title1"
                      className={getHeaderTextColor()}
                    >
                      {title}
                    </Typography>
                    {rightDrawer && (
                      <Button
                        className="order-first md:order-last 3xl:hidden"
                        variant="outline"
                        component="button"
                        type="button"
                        onClick={() => setShowRightDrawer(true)}
                      >
                        {rightDrawer.viewActionText || 'View More'}
                      </Button>
                    )}
                  </div>
                  {header && (
                    <div
                      className={`page-header-content ${getHeaderTextColor()}`}
                    >
                      {header}
                    </div>
                  )}
                </div>
                <div className="page-content">{children}</div>
              </Stack>
            </Box>
            {afterContent && <div className="p-4 md:p-6">{afterContent}</div>}
            {footer && <div className="page-footer">{footer}</div>}
          </Stack>
        </div>
      )}
      {!!rightDrawer && variant !== 'coach-services' && (
        <div
          {...(showRightDrawer
            ? {
                role: 'dialog',
                'aria-modal': 'true',
                'aria-label': rightDrawer.title || 'Panel',
              }
            : {})}
          className={`fixed right-0 top-0 z-40 h-screen w-96 overflow-y-auto p-4 transition-transform 3xl:relative 3xl:h-full 3xl:w-full 3xl:translate-x-0 3xl:p-0 ${
            showRightDrawer ? 'transform-none' : 'translate-x-full'
          }`}
        >
          <Box
            {...{
              ...getBoxVariant(),
              color:
                variant === 'select' ? 'select-light' : getBoxVariant().color,
            }}
          >
            <Stack spacing={4}>
              <div className="flex items-start justify-between gap-2">
                <Typography variant="subtitle">
                  {rightDrawer.title || ''}
                </Typography>
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="rounded p-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-blue-medium-700 focus-visible:ring-offset-2 3xl:hidden"
                  aria-label={`Close ${rightDrawer.title || 'panel'}`}
                  onClick={() => setShowRightDrawer(false)}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div>{rightDrawer.children}</div>
            </Stack>
          </Box>
        </div>
      )}
    </div>
  )
}

export default Page
